import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { GenericModal } from "@/components/GenericModal/GenericModal";
import { GenericButton } from "@/components/GenericButton/GenericButton";
import { OrphanSaleCandidate, searchOrphanSales } from "@/api/sales";
import { reconcileOrphanBill } from "@/api/flashscan";
import { resolveBillManually } from "@/api/bills";

import "./FlashscanOrphanBillModal.scss";

export type OrphanBillTarget = {
  id: string;
  providerId: string;
  accountName: string;
  amount: number;
  startPeriod?: string;
  endPeriod?: string;
  soldItemsDescription?: string;
};

export const FlashscanOrphanBillModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  target: OrphanBillTarget | null;
  onResolved: () => Promise<void> | void;
}> = ({ isOpen, onClose, target, onResolved }) => {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<OrphanSaleCandidate[]>([]);
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);
  const [ignoreComment, setIgnoreComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const billPeriodLabel = useMemo(() => {
    if (!target?.startPeriod || !target?.endPeriod) return "-";
    const start = new Date(target.startPeriod);
    const end = new Date(target.endPeriod);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "-";
    return `${start.toLocaleDateString("fr-FR")} → ${end.toLocaleDateString(
      "fr-FR",
    )}`;
  }, [target?.endPeriod, target?.startPeriod]);

  const close = useCallback(() => {
    setQ("");
    setResults([]);
    setSelectedSaleId(null);
    setIgnoreComment("");
    setIsLoading(false);
    setIsSubmitting(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    void (async () => {
      setIsLoading(true);
      try {
        const res = await searchOrphanSales("");
        setResults(res.data);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = window.setTimeout(() => {
      void (async () => {
        const query = q.trim();
        if (query.length > 0 && query.length < 3) return;

        setIsLoading(true);
        try {
          const res = await searchOrphanSales(query);
          setResults(res.data);
        } finally {
          setIsLoading(false);
        }
      })();
    }, 250);

    return () => window.clearTimeout(handle);
  }, [isOpen, q]);

  const onIgnore = useCallback(async () => {
    if (!target) return;
    setIsSubmitting(true);
    try {
      await resolveBillManually(target.id, {
        action: "IGNORE",
        userComment: ignoreComment.trim() || null,
      });
      await onResolved();
      close();
    } finally {
      setIsSubmitting(false);
    }
  }, [close, ignoreComment, onResolved, target]);

  const onReconcile = useCallback(async () => {
    if (!target || !selectedSaleId) return;
    setIsSubmitting(true);
    try {
      await reconcileOrphanBill(target.id, selectedSaleId);
      await onResolved();
      close();
    } finally {
      setIsSubmitting(false);
    }
  }, [close, onResolved, selectedSaleId, target]);

  return (
    <GenericModal
      isOpen={isOpen}
      title={target?.accountName || t("pages.flashscan.orphanBill.titleFallback")}
      onClose={close}
      className="flashscan-orphan-bill-modal"
    >
      {target ? (
        <div className="flashscan-orphan-bill-modal-content">
          <div className="flashscan-orphan-bill-modal-section-title">
            {t("pages.flashscan.orphanBill.orphanDataTitle")}
          </div>
          <div className="flashscan-orphan-bill-modal-orphan-card">
            <div className="flashscan-orphan-bill-modal-orphan-grid">
              <div>
                <div className="flashscan-orphan-bill-modal-label">
                  {t("pages.flashscan.orphanBill.fields.client")}
                </div>
                <div className="flashscan-orphan-bill-modal-value">
                  {target.accountName}
                </div>
              </div>
              <div>
                <div className="flashscan-orphan-bill-modal-label">
                  {t("pages.flashscan.orphanBill.fields.date")}
                </div>
                <div className="flashscan-orphan-bill-modal-value">
                  {billPeriodLabel}
                </div>
              </div>
              <div>
                <div className="flashscan-orphan-bill-modal-label">
                  {t("pages.flashscan.orphanBill.fields.amount")}
                </div>
                <div className="flashscan-orphan-bill-modal-value">
                  {target.amount.toLocaleString("fr-FR")} €
                </div>
              </div>
              <div>
                <div className="flashscan-orphan-bill-modal-label">
                  {t("pages.flashscan.orphanBill.fields.reference")}
                </div>
                <div className="flashscan-orphan-bill-modal-value">
                  {target.providerId}
                </div>
              </div>
            </div>
          </div>

          <div className="flashscan-orphan-bill-modal-section-title">
            {t("pages.flashscan.orphanBill.findMatchTitle")}
          </div>

          <div className="flashscan-orphan-bill-modal-search-row">
            <input
              className="flashscan-orphan-bill-modal-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("pages.flashscan.orphanBill.searchPlaceholder")}
            />
          </div>

          <div className="flashscan-orphan-bill-modal-results">
            {isLoading ? (
              <div className="flashscan-orphan-bill-modal-muted">
                {t("pages.flashscan.orphanBill.loading")}
              </div>
            ) : results.length === 0 ? (
              <div className="flashscan-orphan-bill-modal-muted">
                {t("pages.flashscan.orphanBill.noResults")}
              </div>
            ) : (
              results.map((sale) => {
                const isSelected = sale.id === selectedSaleId;
                const d = new Date(sale.closeDate);
                const dateLabel = Number.isNaN(d.getTime())
                  ? "-"
                  : d.toLocaleDateString("fr-FR");
                return (
                  <button
                    key={sale.id}
                    type="button"
                    className={`flashscan-orphan-bill-modal-result ${
                      isSelected ? "is-selected" : ""
                    }`}
                    onClick={() =>
                      setSelectedSaleId((prev) => (prev === sale.id ? null : sale.id))
                    }
                  >
                    <div className="flashscan-orphan-bill-modal-result-main">
                      <div className="flashscan-orphan-bill-modal-result-title">
                        {sale.accountName ?? "-"}
                      </div>
                      <div className="flashscan-orphan-bill-modal-result-subtitle">
                        {sale.providerId}
                      </div>
                      <div className="flashscan-orphan-bill-modal-result-subtitle">
                        {sale.name}
                      </div>
                      <div className="flashscan-orphan-bill-modal-result-subtitle">
                        {dateLabel}
                      </div>
                    </div>
                    <div className="flashscan-orphan-bill-modal-result-amount">
                      {sale.amount.toLocaleString("fr-FR")} €
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="flashscan-orphan-bill-modal-comment">
            <div className="flashscan-orphan-bill-modal-comment-label">
              {t("pages.flashscan.orphanBill.commentLabel")}
            </div>
            <textarea
              className="flashscan-orphan-bill-modal-comment-input"
              rows={3}
              value={ignoreComment}
              onChange={(e) => setIgnoreComment(e.target.value)}
              placeholder={t("pages.flashscan.orphanBill.commentPlaceholder")}
            />
          </div>

          <div className="flashscan-orphan-bill-modal-footer">
            <button
              type="button"
              className="flashscan-orphan-bill-modal-ignore"
              onClick={() => void onIgnore()}
              disabled={isSubmitting}
            >
              {t("pages.flashscan.orphanBill.actions.ignore")}
            </button>
            <GenericButton
              label={t("pages.flashscan.orphanBill.actions.reconcile")}
              onClick={() => void onReconcile()}
              disabled={!selectedSaleId || isSubmitting}
              theme="primary"
            />
          </div>
        </div>
      ) : null}
    </GenericModal>
  );
};

